import type { ComponentLike } from '@glint/template';
import type { PartnerPersona } from 'tio-employee/utils/partner-dashboard/personas';

/** The four dashboard sections, in render order (Figma "Employee Home" layout). */
export const DASHBOARD_SECTIONS = ['hero', 'attention', 'pertinent', 'explore'] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/** What every widget's `requires` and `load` receive. */
export interface DashboardContext {
  personas: ReadonlySet<PartnerPersona>;
  /** Current company id; empty when the session has no company. */
  companyId: string;
}

/** Signature every widget component implements; `data` is its own `load()` result. */
export interface WidgetComponentSignature<Data> {
  Args: {
    /** The resolved result of this widget's own `load()`; the frame renders the widget only once it resolves. */
    data: Data;
  };
}

export interface WidgetDefinition<Data> {
  /** Stable key; also the widget's test-hook suffix. */
  id: string;
  section: DashboardSection;
  /** The widget renders for a viewer holding any of these personas. */
  drivenBy: readonly PartnerPersona[];
  /** Extra gate, e.g. a product entitlement; the widget is skipped when this returns false. */
  requires?: (ctx: DashboardContext) => boolean;
  /** Fetches the widget's data. Called once per route visit and never awaited by the route. */
  load: (ctx: DashboardContext) => Promise<Data>;
  component: ComponentLike<WidgetComponentSignature<Data>>;
}

/** A registry entry with its data type erased, so widgets of different shapes share one list. */
export type RegisteredWidget = WidgetDefinition<unknown>;

/**
 * Registers a widget. Type-checks `load` against `component` at the definition
 * site, then erases `Data` so the entry fits the shared registry list.
 */
export function defineWidget<Data>(definition: WidgetDefinition<Data>): RegisteredWidget {
  return definition as unknown as RegisteredWidget;
}

/**
 * Widgets the viewer sees, in section order and then registry order. A widget
 * is included when the viewer holds any persona in its `drivenBy` and its
 * `requires` gate (if any) passes.
 */
export function widgetsFor(
  registry: readonly RegisteredWidget[],
  ctx: DashboardContext
): RegisteredWidget[] {
  const visible = registry.filter(
    (widget) =>
      widget.drivenBy.some((persona) => ctx.personas.has(persona)) &&
      (widget.requires?.(ctx) ?? true)
  );
  return DASHBOARD_SECTIONS.flatMap((section) =>
    visible.filter((widget) => widget.section === section)
  );
}
